import { LucideMinus } from 'lucide-react'
import React, { useEffect, useRef, useState } from 'react'
import { useIMask } from 'react-imask'
import { Button, Input } from '~/components/ui'
import { cn } from '~/lib'

function MaskedInput({
  label,
  value,
  updateValue,
  className,
}: {
  label: string
  value: number | undefined
  updateValue: (value: number | undefined) => void
  className?: string
}) {
  const {
    ref,
    value: maskedValue,
    typedValue,
    setTypedValue,
  } = useIMask(
    {
      mask: Number,
      thousandsSeparator: ',',
      scale: 2,
      min: 0,
      radix: '.',
    },
    { defaultTypedValue: value }
  )

  useEffect(() => {
    if (!value) {
      setTypedValue('' as any) // clears 0 initial value
    }
  }, [])

  useEffect(() => {
    updateValue(typedValue === 0 ? undefined : typedValue)
  }, [maskedValue])

  return (
    <Input
      ref={ref as React.Ref<HTMLInputElement>}
      placeholder={label}
      defaultValue={value || undefined}
      className={className}
    />
  )
}

export default function MapMinMaxFilter({
  className,
  minName,
  maxName,
  label,
  start,
  end,
  min,
  max,
  updateFilter,
}: {
  className?: string
  minName: string
  maxName: string
  label: string
  start?: React.ReactNode
  end?: React.ReactNode
  min?: number
  max?: number
  updateFilter: (key: string, value: any) => void
}) {
  const [resetKey, setResetKey] = useState(Date.now())
  const minValue = useRef<number | undefined>(min)
  const maxValue = useRef<number | undefined>(max)

  function handleMinValue(value: number | undefined) {
    minValue.current = value
    updateFilter(minName, value)
  }

  function handleMaxValue(value: number | undefined) {
    maxValue.current = value
    updateFilter(maxName, value)
  }

  return (
    <div className={cn('space-y-2', className)}>
      <p className="text-sm">
        {label}
        <Button
          variant="link"
          size="none"
          className="ml-2"
          onClick={(e) => {
            e.preventDefault()
            handleMaxValue(undefined)
            handleMinValue(undefined)
            setResetKey(Date.now())
          }}>
          (reset)
        </Button>
      </p>
      <div className="flex items-center justify-stretch">
        <div className="relative">
          {start && (
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <span className="text-muted-foreground sm:text-sm">{start}</span>
            </div>
          )}
          <MaskedInput
            key={resetKey}
            label="Min"
            value={minValue.current}
            updateValue={handleMinValue}
            className={cn(start && 'pl-7', end && 'pr-12')}
          />
          {end && (
            <div className="pointer-events-none absolute inset-y-0 right-0 z-0 flex items-center pr-3">
              <span className="text-muted-foreground sm:text-sm">{end}</span>
            </div>
          )}
        </div>
        <div className="mx-2 text-muted-foreground">
          <LucideMinus className="h-3 w-3" />
        </div>
        <div className="relative">
          {start && (
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <span className="text-muted-foreground sm:text-sm">{start}</span>
            </div>
          )}
          <MaskedInput
            key={resetKey}
            label="Max"
            value={maxValue.current}
            updateValue={handleMaxValue}
            className={cn(start && 'pl-7', end && 'pr-12')}
          />
          {end && (
            <div className="pointer-events-none absolute inset-y-0 right-0 z-0 flex items-center pr-3">
              <span className="text-muted-foreground sm:text-sm">{end}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
